export const STATUS_MESSAGES = [
  {
    en: "Someone nearby is typing quietly.",
    zh: "旁边有人在轻轻敲键盘。",
  },
  {
    en: "The barista sets your cup down on the table.",
    zh: "咖啡师把你的那杯轻轻放到桌上。",
  },
  {
    en: "A student by the window turns a page.",
    zh: "窗边的学生翻了一页书。",
  },
  {
    en: "The room is quiet. Stay with what's in front of you.",
    zh: "店里很安静。留在你手上的事里。",
  },
  {
    en: "The people around you are settling in too.",
    zh: "周围的人也慢慢进入状态了。",
  },
  {
    en: "Your seat is still here. Start with one small step.",
    zh: "座位还在。先回到一个很小的步骤。",
  },
];

export const PAUSE_INTERVENTIONS = {
  window: {
    speaker: { en: "Reader by the window", zh: "窗边顾客" },
    line: { en: "You looked very focused earlier.", zh: "刚才看你一直很认真。" },
    thought: {
      en: (task) => `I came here for “${task}.” One small step gets me back.`,
      zh: (task) => `先别散。回到「${task}」的下一小步。`,
    },
  },
  corner: {
    speaker: { en: "Person at the next table", zh: "旁桌顾客" },
    line: { en: "The room is still quiet.", zh: "这里还是挺安静的。" },
    thought: {
      en: (task) => `I'm not drifting at home. I'm here, and “${task}” is still next.`,
      zh: (task) => `我不是在家里躺着，我已经坐在这里了。回到「${task}」。`,
    },
  },
  bar: {
    speaker: { en: "Barista", zh: "咖啡师" },
    line: { en: "Your cup is still here. Take your time.", zh: "你的杯子还在这边。慢慢来。" },
    thought: {
      en: (task) => `I've already arrived. One small step on “${task}.”`,
      zh: (task) => `已经到这儿了。先做「${task}」里很小的一步。`,
    },
  },
  quiet: {
    speaker: { en: "Me", zh: "我" },
    line: { en: "This seat is still holding the space for me.", zh: "这个座位还在替我留住空间。" },
    thought: {
      en: (task) => `I'm already here. Back to “${task}” before the thread goes.`,
      zh: (task) => `我已经在这里了。趁线索还在，先回到「${task}」。`,
    },
  },
};

// Offered once per session after 25 focused minutes. Same tone as the pause nudge: permission, not failure.
export const BREAK_INTERVENTIONS = {
  window: {
    speaker: { en: "Reader by the window", zh: "窗边顾客" },
    line: { en: "The reader by the window turns a page and looks outside.", zh: "窗边的人翻过一页书，往窗外看了一会儿。" },
    thought: {
      en: (task, minutes) =>
        `${minutes} minutes with “${task}.” A look out the window, then the next step.`,
      zh: (task, minutes) => `已经和「${task}」待了 ${minutes} 分钟。看一会儿窗外，再决定下一步。`,
    },
  },
  corner: {
    speaker: { en: "Person at the next table", zh: "旁桌顾客" },
    line: { en: "The person at the next table gets up for a refill.", zh: "旁桌的人起身去续杯了。" },
    thought: {
      en: (task, minutes) => `${minutes} minutes in. I can stretch for a bit and come back to “${task}.”`,
      zh: (task, minutes) => `${minutes} 分钟了。起来活动一下，再回到「${task}」。`,
    },
  },
  bar: {
    speaker: { en: "Barista", zh: "咖啡师" },
    line: { en: "Want a top-up of hot water?", zh: "要不要再加一点热水？" },
    thought: {
      en: (task, minutes) => `${minutes} minutes of focus. A short break is fair.`,
      zh: (task, minutes) => `专注了 ${minutes} 分钟，歇一下是应该的。`,
    },
  },
  quiet: {
    speaker: { en: "Me", zh: "我" },
    line: { en: "I have been sitting here a while.", zh: "已经坐了很久了。" },
    thought: {
      en: (task, minutes) => `${minutes} minutes of focus. A sip of water, eyes off the screen for a bit, then back to “${task}.”`,
      zh: (task, minutes) => `专注了 ${minutes} 分钟。喝口水，眼睛离开屏幕一会儿，再回到「${task}」。`,
    },
  },
};

export const COPY = {
  en: {
    appName: "Virtual Café Focus Room",
    siteNote: "Everything you do here stays in this browser.",
    privacyLink: "Privacy",
    feedbackLink: "Feedback",
    languageLabel: "Language",
    currentScene: "Current scene",
    sceneNames: {
      entrance: "Door",
      order: "Counter",
      seat: "Seat",
      setup: "Setup",
      focus: "Focus",
      complete: "Done",
    },
    entranceEyebrow: "Café mode",
    entranceTitle: "Your table is waiting.",
    entranceLead:
      "Leave home mode behind. Order something, pick a seat, and let the room keep you company while you work.",
    enterCafe: "Step inside",
    presenceAria: "Café ambience",
    presence: ["the door bell", "low voices", "cups set down on wood"],
    orderEyebrow: "At the counter",
    orderTitle: "Order something. That's how you arrive.",
    barista: "Barista",
    baristaGreeting: "Hi, welcome in. What can I get you?",
    baristaResponse: "Got it. Grab a seat and I’ll bring it over.",
    chooseSeat: "Choose a seat",
    seatEyebrow: "Find your table",
    seatTitle: "What kind of quiet do you need today?",
    back: "Back",
    sitDown: "Sit down",
    fingerprintAria: "How this seat sounds",
    fingerprint: { cafe: "voices", rain: "rain", traffic: "street" },
    setupEyebrow: (seatName) => `At your ${seatName?.toLowerCase() ?? "table"}`,
    setupTitle: "What are you here to work on?",
    changeChoice: "Change",
    howLong: "How long",
    whenNote: "changes the sound only",
    beforeSit: "Before you sit down",
    optional: "optional",
    durationHint: (min, max) => `Between ${min} and ${max} minutes`,
    taskLabel: "Task",
    taskPlaceholder: "One line is enough, like: write the intro",
    durationAria: "Focus duration",
    minuteShort: "min",
    customDuration: "Custom",
    ritualPhone: "Put my phone away",
    ritualLaptop: "Open my laptop",
    minutes: "minutes",
    startWorking: "Start working",
    remainingAria: (time) => `Remaining time ${time}`,
    pause: "Pause",
    resume: "Resume",
    end: "End",
    endConfirm: "End the session?",
    pausedLabel: "Paused",
    mainTimerPaused: (time) => `Main timer paused at ${time}`,
    roomControls: "Room controls",
    slotCycle: "Change the time of day",
    mixerTitle: "Room sound",
    closeMixer: "Close",
    innerThought: "Inner thought",
    keyboardHint: "Space to pause or resume. Esc pauses, then ends.",
    breakOfferAria: "Break suggestion",
    breakTake: "Take 5 minutes",
    breakSkip: "Keep going",
    breakEyebrow: "On a break",
    breakTitle: "Away from the table for a few minutes.",
    breakHint: (task) => `“${task}” will be right here when you’re back.`,
    breakReturn: "Back to my table",
    breakOverLine: "Cup’s back on the table. One small step to start.",
    returnToTask: "Back to it",
    detailsAria: "Session details and ambience",
    drink: "Drink",
    roomTone: "Room tone",
    ambienceOn: "Room sound on",
    startAmbience: "Turn the room sound on",
    muteAmbience: "Mute the café",
    unmuteAmbience: "Unmute the café",
    mixerShow: "Adjust the room sound",
    mixerHide: "Hide the room sound",
    soundLabels: {
      cafe: "Café voices",
      rain: "Rain",
      keys: "Typing",
      cups: "Cups and stirring",
      traffic: "Street",
      backCounter: "Back counter",
      birds: "Birdsong",
      jazz: "Soft jazz",
    },
    timeSlotLabel: "Time of day",
    timeSlots: {
      morning: "Morning",
      day: "Daytime",
      night: "Night",
    },
    jazzPaused: "Off for now while we tune the playlists. The YouTube station below still plays.",
    jazzModeLabel: "Jazz mood",
    jazzModes: {
      cafe: "Cafe",
      swing: "Swing",
      club: "Club",
    },
    playerTitle: "Music",
    sourceLabel: "Source",
    prevTrack: "Previous track",
    nextTrack: "Next track",
    trackFailed: "This one won’t play right now. Skip to the next.",
    jazzNote: "Six stations of licensed recordings, hosted by the café. Jazz and YouTube can’t play together.",
    playMusic: "Play",
    pauseMusic: "Pause the music",
    playerExpand: "Open the music player",
    playerCollapse: "Collapse the music player",
    playerCollapseNote: "The video stays on screen while it plays, so collapsing the player pauses it.",
    musicInPlayer: "Stations and links live in the player at the bottom right.",
    youtubeLabel: "YouTube station",
    youtubeStationsAria: "YouTube stations",
    youtubeCustom: "My link",
    youtubePaste: "Paste a YouTube link",
    youtubeUse: "Play",
    youtubeInvalid: "That doesn't look like a YouTube video link.",
    youtubeNote: "Turning this up pauses the jazz. YouTube may play ads.",
    youtubeIdle: "Start ambience to play this station.",
    youtubeUnavailable:
      "This video can't play here. The owner may block other sites, or the stream has ended. Try another station or link.",
    sessionComplete: "Session complete",
    sessionEnded: "Ended early",
    stayedWith: (task) => `You stayed with “${task}.”`,
    completedLine: (minutes) => `${minutes} minutes at the table. That counts.`,
    endedLine: "A little while still counts. Your table is here whenever you want it.",
    restoredLine: "Your table was kept for you.",
    visitAgain: "Visit again",
    stayAgain: (minutes) => `Stay another ${minutes} minutes`,
    visitEyebrow: (n) => `Welcome back · visit ${n}`,
    stampCard: "Stamp card",
    cardNumber: (n) => `card ${n}`,
    stampsAria: (filled, total) => `${filled} of ${total} stamps`,
    visitTotal: (n) => (n === 1 ? "1 visit" : `${n} visits`),
    totalFocused: (time) => `${time} focused in all`,
    stampEarned: "One more stamp on your card.",
    stampMissed: "Stamps come with 10 minutes or a finished session.",
    cardFull: "Card complete. A fresh one next time.",
    hoursMinutes: (h, m) => (m ? `${h} h ${m} min` : `${h} h`),
    minutesOnly: (m) => `${m} min`,
  },
  zh: {
    appName: "云咖啡馆专注室",
    siteNote: "你的记录只留在这个浏览器里。",
    privacyLink: "隐私",
    feedbackLink: "反馈",
    languageLabel: "语言",
    currentScene: "当前步骤",
    sceneNames: {
      entrance: "入口",
      order: "点单",
      seat: "选座",
      setup: "设定",
      focus: "专注",
      complete: "完成",
    },
    entranceEyebrow: "出门，去咖啡馆",
    entranceTitle: "你的座位在等你。",
    entranceLead:
      "换个地方做事。点一杯，挑个位子，让这间店陪你专注一会儿。",
    enterCafe: "推门进去",
    presenceAria: "咖啡馆氛围",
    presence: ["门铃", "低低的交谈声", "杯子放到木桌上"],
    orderEyebrow: "在吧台",
    orderTitle: "先点一杯，算是真的到了。",
    barista: "咖啡师",
    baristaGreeting: "欢迎。今天想喝点什么？",
    baristaResponse: "好，去找个位子坐，我给你端过去。",
    chooseSeat: "去挑座位",
    seatEyebrow: "找座位",
    seatTitle: "今天想要哪一种安静？",
    back: "返回",
    sitDown: "坐下来",
    fingerprintAria: "这个座位的声音",
    fingerprint: { cafe: "人声", rain: "雨", traffic: "街" },
    setupEyebrow: (seatName) => `在${seatName ?? "座位"}`,
    setupTitle: "今天来这里做什么？",
    changeChoice: "改",
    howLong: "多久",
    whenNote: "只影响声音，不换画面",
    beforeSit: "坐下之前",
    optional: "可选",
    durationHint: (min, max) => `${min} 到 ${max} 分钟`,
    taskLabel: "任务",
    taskPlaceholder: "写一句就行，比如：写引言",
    durationAria: "专注时长",
    minuteShort: "分钟",
    customDuration: "自定义",
    ritualPhone: "把手机放远一点",
    ritualLaptop: "打开电脑",
    minutes: "分钟",
    startWorking: "开始专注",
    remainingAria: (time) => `剩余时间 ${time}`,
    pause: "暂停",
    resume: "继续",
    end: "结束",
    endConfirm: "确定结束？",
    pausedLabel: "已暂停",
    mainTimerPaused: (time) => `主计时 ${time} 已暂停`,
    roomControls: "房间控制",
    slotCycle: "切换时间档",
    mixerTitle: "环境音",
    closeMixer: "关闭",
    innerThought: "我心想",
    keyboardHint: "空格暂停或继续。Esc 先暂停，再按一次结束。",
    breakOfferAria: "休息提示",
    breakTake: "休息 5 分钟",
    breakSkip: "继续",
    breakEyebrow: "休息中",
    breakTitle: "离开桌子一会儿。",
    breakHint: (task) => `「${task}」几分钟后还在这里。`,
    breakReturn: "回到座位",
    breakOverLine: "杯子放回桌上。先从一个很小的步骤开始。",
    returnToTask: "回到任务",
    detailsAria: "本次专注和环境音",
    drink: "饮品",
    roomTone: "座位氛围",
    ambienceOn: "环境音开着",
    startAmbience: "打开环境音",
    muteAmbience: "静音",
    unmuteAmbience: "打开声音",
    mixerShow: "调整环境音",
    mixerHide: "收起环境音",
    soundLabels: {
      cafe: "咖啡厅人声",
      rain: "雨",
      keys: "敲键盘",
      cups: "杯子和搅拌",
      traffic: "街道",
      backCounter: "后厨",
      birds: "鸟叫",
      jazz: "轻爵士",
    },
    timeSlotLabel: "时间段",
    timeSlots: {
      morning: "早晨",
      day: "白天",
      night: "夜晚",
    },
    jazzPaused: "正在优化，暂时关闭。下面的 YouTube 电台照常可用。",
    jazzModeLabel: "爵士氛围",
    jazzModes: {
      cafe: "咖啡馆",
      swing: "摇摆",
      club: "小酒馆",
    },
    playerTitle: "音乐",
    sourceLabel: "来源",
    prevTrack: "上一首",
    nextTrack: "下一首",
    trackFailed: "这首暂时放不了，跳到下一首试试。",
    jazzNote: "六个电台都是有授权的录音，由咖啡馆自己托管。爵士和 YouTube 不能同时开。",
    playMusic: "播放",
    pauseMusic: "暂停音乐",
    playerExpand: "展开播放器",
    playerCollapse: "收起播放器",
    playerCollapseNote: "播放时视频要保持可见，所以收起播放器会暂停。",
    musicInPlayer: "电台和链接在右下角的播放器里。",
    youtubeLabel: "YouTube 电台",
    youtubeStationsAria: "YouTube 电台列表",
    youtubeCustom: "我的链接",
    youtubePaste: "粘贴一个 YouTube 链接",
    youtubeUse: "播放",
    youtubeInvalid: "这个看起来不是 YouTube 视频链接。",
    youtubeNote: "打开电台会自动关掉爵士。YouTube 可能会插播广告。",
    youtubeIdle: "开启环境音后开始播放。",
    youtubeUnavailable: "这个视频无法在这里播放，可能是作者禁止了外站播放，或者直播已经结束。换一个电台或链接试试。",
    sessionComplete: "专注完成",
    sessionEnded: "提前结束",
    stayedWith: (task) => `你陪着「${task}」坐了一会儿。`,
    completedLine: (minutes) => `整整 ${minutes} 分钟。这已经算数。`,
    endedLine: "待了一会儿也算数。座位还在，想回来随时回来。",
    restoredLine: "座位一直给你留着。",
    visitAgain: "再来一次",
    stayAgain: (minutes) => `再坐 ${minutes} 分钟`,
    visitEyebrow: (n) => `欢迎回来 · 第 ${n} 次来`,
    stampCard: "集点卡",
    cardNumber: (n) => `第 ${n} 张`,
    stampsAria: (filled, total) => `已集 ${filled} 个章，共 ${total} 格`,
    visitTotal: (n) => `已来过 ${n} 次`,
    totalFocused: (time) => `累计专注 ${time}`,
    stampEarned: "卡上多了一个章。",
    stampMissed: "专注满 10 分钟或完成一次就能盖章。",
    cardFull: "这张卡集满了，下次换一张新的。",
    hoursMinutes: (h, m) => (m ? `${h} 小时 ${m} 分钟` : `${h} 小时`),
    minutesOnly: (m) => `${m} 分钟`,
  },
};
